import { useEffect, useState } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Shield, LayoutDashboard, Building2, ScrollText, LogOut, ChevronRight, LineChart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { superAdminApi } from '../../lib/superAdminApi';

const NAV = [
  { path: '/super-admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/super-admin/tenants', label: 'Tenants', icon: Building2 },
  { path: '/super-admin/insights', label: 'Insights', icon: LineChart },
  { path: '/super-admin/audit-log', label: 'Audit Log', icon: ScrollText },
];

export default function SuperAdminLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('super_admin_token')) {
      nav('/super-admin/login');
      return;
    }
    superAdminApi.me().then((r) => setMe(r.data)).catch(() => nav('/super-admin/login'));
  }, [nav]);

  const logout = () => {
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin');
    nav('/super-admin/login');
  };

  return (
    <div className="min-h-screen text-slate-100 flex" style={{ background: '#0D1B3E', fontFamily: "'Outfit', 'Inter', sans-serif" }} data-testid="super-admin-layout">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/10 flex flex-col" style={{ background: '#0D1B3E' }}>
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(33,150,201,0.2)' }}>
              <Shield className="w-5 h-5" style={{ color: '#5BB8E8' }} />
            </div>
            <div>
              <div className="font-bold tracking-tight text-[15px]">Super Admin</div>
              <div className="text-[9px] tracking-[0.2em] uppercase" style={{ color: '#5BB8E8' }}>NIS2AI Hub</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 cu-scroll overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = loc.pathname.startsWith(n.path);
            return (
              <Link
                key={n.path}
                to={n.path}
                data-testid={`sa-nav-${n.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-[13px] font-medium transition-colors ${
                  active
                    ? 'text-white shadow-sm'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
                style={active ? { background: '#2196C9' } : {}}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{n.label}</span>
                {active && <ChevronRight className="w-4 h-4" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          {me && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: '#2196C9' }}>
                {me.name?.[0]?.toUpperCase() || 'S'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-white/90 text-[13px] truncate" data-testid="sa-user-name">{me.name}</div>
                <div className="text-[11px] text-white/50 truncate">{me.email}</div>
              </div>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="w-full bg-transparent border-white/20 text-white/80 hover:bg-white/10 hover:text-white"
            data-testid="sa-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" /> Terminar sessão
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto" style={{ background: '#0a0e1a' }}>
        <div className="p-8 max-w-7xl mx-auto cu-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
